import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, User, X } from '@phosphor-icons/react';
import type { InviteDto } from '@reelplicate/api-client/model';
import { OnboardingStep } from '@reelplicate/api-client/model';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Avatar,
  Badge,
  Button,
  IconButton,
  Inline,
  Link,
  Select,
  SelectItem,
  Stack,
  TagInput,
  toast,
} from '@reelplicate/ui';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { fromApiRole, roleName } from '@/features/team/roles';
import { useInvites } from '@/features/team/use-invites';

import { OnboardingActions, OnboardingCard, OnboardingHeader } from './onboarding-card';
import { useOnboardingProgress } from './progress';
import { isEmail, TeamStepSchema, type TeamStepValues } from './schemas';
import { finishPath, ONBOARDING_STEPS } from './steps';

// Settings → Billing isn't open mid-onboarding: a bigger plan is picked on the Plan step.
const UPGRADE_PATH = '/onboarding/plan';

/**
 * Figma "Member Row" (107:86), pending invite: 56px, 12 / 8px sides - an icon
 * Avatar, the email with a warning "Invited" Badge over the role (Caption
 * text/tertiary), a small brand "Resend" link and a ghost revoke Icon Button.
 */
function PendingInvite({
  invite,
  onResend,
  onRevoke,
}: {
  invite: InviteDto;
  onResend: () => void;
  onRevoke: () => void;
}) {
  return (
    <li className="flex h-14 items-center gap-3 rounded-md pr-2 pl-3">
      <Avatar icon={User} alt="" size={32} />
      <Stack gap={0} className="min-w-0 flex-1">
        <Inline className="gap-1.5">
          <span className="text-body-md-strong truncate">{invite.email}</span>
          <Badge tone="warning">Invited</Badge>
        </Inline>
        <span className="text-caption text-text-tertiary truncate">
          {roleName(fromApiRole(invite.role))}
        </span>
      </Stack>
      <Link asChild size="sm">
        <button type="button" onClick={onResend}>
          Resend
        </button>
      </Link>
      <IconButton
        icon={X}
        size="sm"
        variant="ghost"
        aria-label={`Revoke invite for ${invite.email}`}
        onClick={onRevoke}
      />
    </li>
  );
}

/**
 * Figma "Onboarding — 6 · Team" (127:1189): a Tag Input of email addresses
 * beside a 132px role Select, a helper line, then "Already invited" with
 * pending Member Rows. "Skip for now" (ghost) and "Send invites"; no Back
 * once the plan is paid.
 */
export function TeamStepPage() {
  const navigate = useNavigate();
  const emailsLabelId = useId();
  const helperId = useId();
  const form = useForm<TeamStepValues>({
    resolver: zodResolver(TeamStepSchema),
    defaultValues: { emails: [], role: 'editor' },
  });
  const { advance, saving } = useOnboardingProgress();
  const { data: workspaces } = useListWorkspaces();
  const { invites, send, sending, resend, revoke } = useInvites(
    workspaces?.[0]?.id,
    UPGRADE_PATH,
  );
  const pending = invites.data ?? [];
  const next = async () => {
    if (await advance(OnboardingStep.SOURCE)) await navigate(finishPath('source'));
  };
  // Nothing typed is the same as skipping; a failed send stays here (toasted).
  const onSubmit = form.handleSubmit(async ({ emails, role }) => {
    if (emails.length > 0 && !(await send(emails, role))) return;
    await next();
  });

  return (
    <OnboardingCard loose onSubmit={onSubmit}>
      <OnboardingHeader
        eyebrow={`Step 6 of ${ONBOARDING_STEPS.length}`}
        title="Invite your team"
        subtitle="Reelplicate works best with a reviewer in the loop. You can always do this later."
      />

      <Stack gap={4}>
        <Stack gap={2}>
          <p id={emailsLabelId} className="text-label-md">
            Email addresses
          </p>
          {/* The role select keeps its own height while the tags wrap onto more lines. */}
          <Stack gap={2} className="tablet:flex-row tablet:items-start">
            <Controller
              name="emails"
              control={form.control}
              render={({ field }) => (
                <TagInput
                  aria-labelledby={emailsLabelId}
                  aria-describedby={helperId}
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  validate={isEmail}
                  onReject={(tag, reason) =>
                    reason === 'invalid' && toast.error(`“${tag}” isn’t an email address`)
                  }
                  // Tags fill the row once added; the hint is only for an empty field.
                  placeholder={field.value.length ? undefined : 'name@company.com'}
                  type="email"
                  className="flex-1"
                />
              )}
            />
            <Controller
              name="role"
              control={form.control}
              render={({ field }) => (
                <Select
                  aria-label="Role for new invites"
                  value={field.value}
                  onValueChange={field.onChange}
                  className="tablet:w-33"
                >
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="reviewer">Reviewer</SelectItem>
                </Select>
              )}
            />
          </Stack>
          <p id={helperId} className="text-caption text-text-secondary">
            Editors create and edit reels. Reviewers approve before anything publishes.
          </p>
        </Stack>

        {pending.length > 0 && (
          <Stack as="section" aria-label="Already invited">
            <p className="text-label-md">Already invited</p>
            <ul>
              {pending.map((invite) => (
                <PendingInvite
                  key={invite.id}
                  invite={invite}
                  onResend={() => resend(invite)}
                  onRevoke={() => revoke(invite)}
                />
              ))}
            </ul>
          </Stack>
        )}
      </Stack>

      {/* No Back: the plan is paid for by now, so there's nothing to go back and change. */}
      <OnboardingActions>
        <Button
          type="button"
          variant="ghost"
          size="lg"
          onClick={() => void next()}
          disabled={saving || sending}
        >
          Skip for now
        </Button>
        <Button
          type="submit"
          size="lg"
          trailingIcon={ArrowRight}
          loading={saving || sending}
        >
          Send invites
        </Button>
      </OnboardingActions>
    </OnboardingCard>
  );
}
